import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Col,
  Row,
  Alert,
  Spinner,
  ButtonGroup,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage, useField } from "formik";
import * as Yup from "yup";
import { BsFillInfoCircleFill } from "react-icons/bs";

import API from "../Utility";

export default function GeneralSettingsView({
  projectObject,
  setProjectObject,
}) {
  let { project } = useParams();
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [submittingForm, setSubmittingForm] = useState(false);
  const [projectConfig, setProjectConfig] = useState(
    projectObject && projectObject.config ? projectObject.config : { theme: {} }
  );

  useEffect(() => {
    setProjectObject({
      ...projectObject,
      ...{
        config: projectConfig,
      },
    });
  }, [projectConfig]);

  let usersMustActivateInitial = true; // default
  let limitUserToOneGroupInitial = false; // default
  if (
    projectObject &&
    projectObject.config &&
    projectObject.config.general &&
    projectObject.config.general.usersMustActivate === false
  ) {
    usersMustActivateInitial = false;
  }
  if (
    projectObject &&
    projectObject.config &&
    projectObject.config.general &&
    projectObject.config.general.limitUserToOneGroup === true
  ) {
    limitUserToOneGroupInitial = true;
  }
  const [usersMustActivate, setUsersMustActivate] = useState(
    usersMustActivateInitial
  );
  const [limitUserToOneGroup, setLimitUserToOneGroup] = useState(
    limitUserToOneGroupInitial
  );

  // Initialize form validation
  let formValidation = {};

  // Initialize form fields
  let formFields = {
    usersMustActivate:
      projectConfig &&
      projectConfig.general &&
      projectConfig.general.usersMustActivate
        ? projectConfig.general.usersMustActivate
        : undefined,
    limitUserToOneGroup:
      projectConfig &&
      projectConfig.general &&
      projectConfig.general.limitUserToOneGroup
        ? projectConfig.general.limitUserToOneGroup
        : undefined,
  };

  const updateProject = async (values) => {
    try {
      const apiCallConfig = {
        url: `/admin/${project}/update`,
        data: values,
      };
      setSubmittingForm(true);
      const response = await API.put(apiCallConfig);
      if (response) {
        if (response === "Project updated successfully") {
          // success
          setSubmitSuccess("General settings updated successfully");
        } else {
          setSubmitError(response.message || response);
        }
        setSubmittingForm(false);
      }
    } catch (error) {
      if (error.response) {
        setSubmitError(error.response.data.message);
      } else {
        setSubmitError("An error occurred, please try again");
      }
      setSubmittingForm(false);
    }
  };

  const handleSubmit = (values) => {
    let formattedData = { config: projectConfig || {} };
    setSubmitSuccess(null);
    setSubmitError(null);
    if (values) {
      if (values.usersMustActivate !== undefined) {
        if (!formattedData.config.general) {
          formattedData.config.general = {};
        }
        formattedData.config.general.usersMustActivate =
          values.usersMustActivate;
      }
      if (values.limitUserToOneGroup !== undefined) {
        if (!formattedData.config.general) {
          formattedData.config.general = {};
        }
        formattedData.config.general.limitUserToOneGroup =
          values.limitUserToOneGroup;
      }
    }
    if (formattedData && formattedData.config) {
      setProjectConfig(formattedData.config);
      // setProjectObject(formattedData);
    }
    updateProject(formattedData);
  };

  return (
    <Row>
      <Col>
        <Formik
          initialValues={formFields}
          validationSchema={Yup.object().shape(formValidation)}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            handleSubmit(values);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue }) => (
            <Form noValidate>
              <div className="form-group">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="delete-tooltip-disabled">
                      <p>Changing this doesn’t update existing records</p>
                    </Tooltip>
                  }
                >
                  <label>
                    <span>
                      Users must activate (default) <BsFillInfoCircleFill />
                    </span>
                  </label>
                </OverlayTrigger>
                <div>
                  <ButtonGroup>
                    <Button
                      onClick={(e) => {
                        setFieldValue("usersMustActivate", true);
                        setUsersMustActivate(true);
                      }}
                      variant={usersMustActivate ? "primary" : "outline-secondary"}
                    >
                      Yes
                    </Button>
                    <Button
                      onClick={(e) => {
                        setFieldValue("usersMustActivate", false);
                        setUsersMustActivate(false);
                      }}
                      variant={!usersMustActivate ? "primary" : "outline-secondary"}
                    >
                      No
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
              <div className="form-group">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="delete-tooltip-disabled">
                      <p>Changing this doesn’t update existing records</p>
                    </Tooltip>
                  }
                >
                  <label>
                    <span>
                      Each user may only be a member of a single group{" "}
                      <BsFillInfoCircleFill />
                    </span>
                  </label>
                </OverlayTrigger>
                <div>
                  <ButtonGroup>
                    <Button
                      onClick={(e) => {
                        setFieldValue("limitUserToOneGroup", true);
                        setLimitUserToOneGroup(true);
                      }}
                      variant={limitUserToOneGroup ? "primary" : "outline-secondary"}
                    >
                      Yes
                    </Button>
                    <Button
                      onClick={(e) => {
                        setFieldValue("limitUserToOneGroup", false);
                        setLimitUserToOneGroup(false);
                      }}
                      variant={!limitUserToOneGroup ? "primary" : "outline-secondary"}
                    >
                      No
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
              {submitError && <Alert variant={"danger"}>{submitError}</Alert>}
              {submitSuccess && (
                <Alert variant={"success"}>{submitSuccess}</Alert>
              )}
              <div className="form-group">
                {submittingForm ? (
                  <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                  </Spinner>
                ) : (
                  <>
                    <button
                      type="submit"
                      className={`mb-2 btn btn-outline-primary ${
                        submittingForm ? "hide" : ""
                      }`}
                      style={{ marginRight: "5px" }}
                      disabled={submittingForm ? true : false}
                    >
                      Save
                    </button>
                  </>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Col>
    </Row>
  );
}
